import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { verifyEmail } from '../store/authSlice';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useToast } from '../hooks/useToast';

const EmailVerification = () => {
    const [resendMessage, setResendMessage] = useState('');
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState('');
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const isOnline = useNetworkStatus();
    const { showToast } = useToast();

    const handleVerify = () => {
        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        // Simulate email verification - replace with actual API call in production
        showToast('Email verified successfully!', 'success');
        dispatch(verifyEmail());
        navigate('/home');
    };

    const handleResendEmail = () => {
        if (!isOnline) {
            setError('No internet connection. Please check your network and try again.');
            return;
        }

        setError('');
        setIsResending(true);

        // Simulate resending verification email - replace with actual API call in production
        setTimeout(() => {
            setIsResending(false);
            setResendMessage('Verification email sent! Please check your inbox.');
            showToast('Verification email sent!', 'success');
            setTimeout(() => setResendMessage(''), 5000);
        }, 1000);
    };

    return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-background-secondary)', padding: 'var(--spacing-2xl) var(--spacing-md)' }}>
            <div className="max-w-md w-full text-center" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
                <div>
                    <div className="mx-auto flex items-center justify-center rounded-full" style={{ height: '4rem', width: '4rem', backgroundColor: 'var(--color-primary-100)' }}>
                        <svg
                            className="h-8 w-8"
                            style={{ color: 'var(--color-primary-600)' }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                    <h2 className="font-extrabold" style={{ marginTop: 'var(--spacing-lg)', fontSize: 'var(--font-size-3xl)', color: 'var(--color-text-primary)' }}>
                        Verify your email
                    </h2>
                    <p style={{ marginTop: 'var(--spacing-sm)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                        We've sent a verification email to{' '}
                        <span className="font-medium" style={{ color: 'var(--color-primary-600)' }}>
                            {user?.email || 'your email'}
                        </span>
                    </p>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)' }}>
                    <p style={{ color: 'var(--color-text-secondary)' }}>
                        Please check your inbox and click the verification link to continue.
                    </p>

                    {error && (
                        <div className="flex items-center justify-center" style={{ backgroundColor: 'var(--color-danger-100)', border: '1px solid var(--color-danger-400)', color: 'var(--color-danger-700)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', gap: 'var(--spacing-sm)' }}>
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    {resendMessage && (
                        <div className="flex items-center justify-center" style={{ backgroundColor: 'var(--color-success-100)', border: '1px solid var(--color-success-400)', color: 'var(--color-success-700)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-md)', gap: 'var(--spacing-sm)' }}>
                            <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>{resendMessage}</span>
                        </div>
                    )}

                    <div style={{ backgroundColor: 'var(--color-gray-100)', borderRadius: 'var(--radius-lg)', padding: 'var(--spacing-md)' }}>
                        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
                            Didn't receive the email? Check your spam folder or click below to
                            resend.
                        </p>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
                        <button
                            onClick={handleResendEmail}
                            disabled={isResending}
                            className="w-full flex justify-center font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ padding: 'var(--spacing-md)', border: '1px solid var(--color-primary-600)', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', color: 'var(--color-primary-600)', backgroundColor: 'var(--color-surface)' }}
                        >
                            {isResending ? 'Sending...' : 'Resend verification email'}
                        </button>

                        <button
                            onClick={handleVerify}
                            className="w-full flex justify-center font-medium text-white"
                            style={{ padding: 'var(--spacing-md)', border: 'none', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-primary-600)' }}
                        >
                            I've verified my email
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailVerification;
